import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import Icon from '../AppIcon';
import { createBillingCheckoutSession } from '../../services/billingService';
import { formatPenceGbp } from '../../utils/orgBilling';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const BillingModal = ({
  open,
  blocking = false,
  organizationId,
  billing,
  onClose,
  onLogout,
  canPay = false,
}) => {
  const [clientSecret, setClientSecret] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startCheckout = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!publishableKey) {
        throw new Error('Card payments are not enabled yet. A Super Admin still needs to connect Stripe.');
      }
      const session = await createBillingCheckoutSession(organizationId);
      setClientSecret(session.clientSecret || session.client_secret || '');
      setQuote({
        quantity: session.quantity,
        unitAmountPence: session.unitAmountPence ?? session.unit_amount_pence,
        monthlyTotalPence: session.monthlyTotalPence
          ?? ((session.unitAmountPence || 0) * (session.quantity || 1)),
        billingModel: session.billingModel || 'per_home',
        productName: session.productName || null,
        productDescription: session.productDescription || null,
      });
    } catch (err) {
      setError(err.message || 'Could not start checkout');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const adminLocked = billing?.phase === 'deactivated' || billing?.accessOverride === 'locked';

  useEffect(() => {
    if (!open) {
      setClientSecret('');
      setQuote(null);
      setError('');
      return undefined;
    }
    if (canPay && !adminLocked) startCheckout();
    return undefined;
  }, [open, canPay, adminLocked, startCheckout]);

  const options = useMemo(() => (clientSecret ? { clientSecret } : null), [clientSecret]);

  if (!open) return null;

  const title = billing?.phase === 'deactivated'
    ? 'Organisation deactivated'
    : billing?.accessOverride === 'locked'
      ? 'Access locked'
      : billing?.phase === 'locked'
        ? 'Set up billing to restore access'
        : billing?.phase === 'grace'
          ? 'Set up billing to keep access'
          : billing?.phase === 'past_due'
            ? 'Update payment to keep access'
            : 'Add a payment method';

  const monthly = quote
    ? formatPenceGbp(quote.monthlyTotalPence ?? ((quote.unitAmountPence || 0) * (quote.quantity || 1)))
    : billing?.unitLabel || null;
  const bedTierQuote = quote?.billingModel === 'bed_tier';

  return (
    <div className="fixed inset-0 z-[16000] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg max-h-[92vh] flex flex-col bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-border flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0b1c28] to-[#00adef] text-white flex items-center justify-center shrink-0">
              <Icon name="CreditCard" size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground">{title}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {billing?.phase === 'grace' || billing?.phase === 'locked'
                  ? 'Without billing set up, your organisation will lose access to MAAP.'
                  : bedTierQuote
                    ? 'One monthly subscription for your organisation plan. No card was taken at enrolment.'
                    : 'Billed monthly. No card was taken at enrolment.'}
              </p>
            </div>
          </div>
          {!blocking && (
            <button type="button" onClick={onClose} className="p-2 hover:bg-surface rounded-xl transition-colors shrink-0">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {quote && (
            <div className="rounded-xl border border-border bg-surface/60 p-4 text-sm">
              {bedTierQuote ? (
                <>
                  <p className="font-semibold text-foreground">
                    {quote.productName || 'MAAP subscription'} · {monthly}/month
                  </p>
                  <p className="text-muted-foreground mt-1">
                    {quote.productDescription || 'Charged after any remaining trial.'}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-foreground">
                    {quote.quantity} care home{quote.quantity === 1 ? '' : 's'} × {formatPenceGbp(quote.unitAmountPence)}
                  </p>
                  <p className="text-muted-foreground mt-1">
                    {monthly} per month after the remaining trial, if any.
                  </p>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <Icon name="AlertCircle" size={15} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {adminLocked && (
            <p className="text-sm text-muted-foreground">
              A Super Admin has locked this organisation. Card payment will not reopen access.
            </p>
          )}

          {!canPay && !adminLocked && (
            <p className="text-sm text-muted-foreground">
              Ask an organisation admin or care home manager to add a card. You can still log out below.
            </p>
          )}

          {canPay && loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Icon name="Loader2" size={18} className="animate-spin text-primary" />
              Preparing secure checkout…
            </div>
          )}

          {canPay && options && stripePromise && (
            <div className="min-h-[280px]">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex flex-col sm:flex-row gap-2 justify-end shrink-0">
          {canPay && !adminLocked && error && (
            <button
              type="button"
              onClick={startCheckout}
              className="px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90"
            >
              Try again
            </button>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-surface flex items-center justify-center gap-2"
          >
            <Icon name="LogOut" size={16} />
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingModal;
